import React, { useState } from "react";
import { ExternalLink } from "lucide-react";

interface Project {
  id: string;
  name: string;
  status: "Shipped" | "Building" | "Paused";
  progress?: number;
  tags: string[];
  description: string;
  link?: string;
}

const projectsData: Project[] = [
  {
    id: "finishai",
    name: "FinishAI",
    status: "Building",
    progress: 85,
    tags: ["AI", "Chatbot", "Support"],
    description: "AI-powered customer support chatbot platform. Helps businesses automate support without losing the human feel.",
    link: "https://finishai.org",
  },
  {
    id: "curvory",
    name: "Curvory",
    status: "Building",
    progress: 65,
    tags: ["PWA", "Camera", "Artists"],
    description: "PWA pose reference camera app for artists. Dark premium UI, overlay rendering, collapsible pose selection.",
    link: "https://curvory.store",
  },
  {
    id: "curvory-saas",
    name: "Curvory SaaS Platform",
    status: "Building",
    progress: 40,
    tags: ["Next.js", "Clerk", "Supabase", "Cloudflare"],
    description: "Subdomain-claiming SaaS. Users claim yourname.curvory.store. Cloudflare DNS automation, Prisma, full auth.",
  },
  {
    id: "harshitpraj-online",
    name: "harshitpraj.online",
    status: "Shipped",
    progress: 100,
    tags: ["React", "Vite", "Tailwind"],
    description: "This site. A live build log, not a portfolio. Updated as I ship things.",
  },
  {
    id: "finishai-website",
    name: "FinishAI Website",
    status: "Shipped",
    progress: 100,
    tags: ["Next.js", "Tailwind", "Vercel"],
    description: "Marketing and product site for FinishAI. Clean, fast, conversion-focused.",
  },
  {
    id: "ringtrack",
    name: "RingTrack",
    status: "Shipped",
    tags: ["PWA", "SVG", "Habit Tracker"],
    description: "Radial habit tracker with SVG circular grid, Framer Motion animations, and localStorage. Built in one session.",
  },
  {
    id: "cursory",
    name: "Cursory",
    status: "Shipped",
    tags: ["AI Tools", "Instagram", "Digital Products"],
    description: "Instagram-based AI tools and resources brand. Sells reel bundles and teaches AI productivity to creators.",
  },
  {
    id: "background-remover",
    name: "Background Remover",
    status: "Shipped",
    tags: ["Browser", "Client-side", "No API"],
    description: "Fully client-side background remover. No uploads, no API keys, runs entirely in the browser.",
  },
  {
    id: "ai-ingredient-checker",
    name: "AI Ingredient Checker",
    status: "Shipped",
    tags: ["Groq API", "Health", "Single-file"],
    description: "Food and cosmetic ingredient safety checker powered by Groq. Single HTML file, no backend.",
  },
  {
    id: "nutrition-coach",
    name: "Nutrition Coach",
    status: "Shipped",
    tags: ["Groq API", "PWA", "AI"],
    description: "Personalized minerals and nutrition coach. Animated onboarding, radial mineral dashboard, localStorage.",
  },
  {
    id: "ai-companion-chat",
    name: "AI Companion Chat",
    status: "Shipped",
    tags: ["Groq API", "WhatsApp-style", "Single-file"],
    description: "WhatsApp-style AI companion chat. Single HTML file using Groq API. Shipped fast.",
  },
];

const ProjectsPage = () => {
  const [filter, setFilter] = useState<"All" | "Shipped" | "Building" | "Paused">("All");

  const filteredProjects = projectsData.filter((p) => {
    if (filter === "All") return true;
    return p.status === filter;
  });

  return (
    <div className="w-full bg-[#0f0f0f] text-[#f2f2f2] min-h-screen">
      <div className="max-w-[1100px] mx-auto px-5 py-[80px] md:py-[120px]">
        {/* Header */}
        <div className="mb-2">
          <span className="text-[11px] uppercase tracking-[0.12em] text-[#555555] border border-[#222222] px-3 py-1 rounded inline-block font-mono">
            ALL PROJECTS
          </span>
        </div>
        <h1
          className="text-[36px] sm:text-[48px] font-bold text-[#f2f2f2] mb-3"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          Everything I've Built
        </h1>
        <p className="text-[15px] text-[#666666] mb-[40px] max-w-xl">
          A complete log of what I've shipped, what I'm building, and what's paused. Updated as things move.
        </p>

        {/* Filter Tabs */}
        <div className="flex items-center space-x-6 mb-[40px] border-b border-[#1a1a1a] pb-4">
          {(["All", "Shipped", "Building", "Paused"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setFilter(tab)}
              className={`text-sm transition-colors pb-2 border-b-2 -mb-[18px] ${
                filter === tab
                  ? "text-white border-white font-medium"
                  : "text-[#555555] border-transparent hover:text-[#cccccc]"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Project Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px]">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="bg-[#161616] border border-[#222222] rounded-[8px] p-[28px] flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 text-[11px] font-mono">
                    <span
                      className={
                        project.status === "Shipped"
                          ? "status-dot-shipped"
                          : project.status === "Building"
                          ? "status-dot-building"
                          : "status-dot-paused"
                      }
                    />
                    <span
                      className={
                        project.status === "Shipped"
                          ? "text-[#22c55e]"
                          : project.status === "Building"
                          ? "text-[#eab308]"
                          : "text-[#555555]"
                      }
                    >
                      {project.status.toUpperCase()}
                    </span>
                  </div>

                  {project.link && (
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#666666] hover:text-white transition-colors"
                      aria-label={`${project.name} link`}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>

                <h3
                  className="text-[20px] font-bold text-[#f2f2f2] mb-2"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  {project.name}
                </h3>

                <p className="text-[14px] text-[#888888] leading-relaxed mb-6">
                  {project.description}
                </p>
              </div>

              <div>
                {/* Tech Tags */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {project.tags.map((tag) => (
                    <span
                      key={tag}
                      className="bg-[#1e1e1e] text-[#888888] text-[11px] px-[10px] py-[4px] rounded-[4px] font-mono"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Progress bar only if Building or Paused or specified */}
                {(project.status === "Building" || project.status === "Paused") && project.progress !== undefined && (
                  <div>
                    <div className="flex justify-between items-center text-[11px] text-[#666666] font-mono mb-2">
                      <span>PROGRESS</span>
                      <span>{project.progress}%</span>
                    </div>
                    <div className="h-[2px] bg-[#222222] rounded-[2px] overflow-hidden w-full">
                      <div
                        className="h-full bg-white rounded-[2px]"
                        style={{ width: `${project.progress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectsPage;
